/* eslint-disable @typescript-eslint/no-unsafe-function-type */
import {
  enhanceError,
  getJwtTokenExpirationDate,
  isJwtTokenExpired,
  isObject,
} from '@allurion/utils';
import { Client, Conversation, JSONObject } from '@twilio/conversations';

import { Logger } from 'src/services/Logger';

import { clearTwilioToken, getTwilioToken, TOKEN_EXPIRY_MARGIN_SECONDS } from './twilio-token';
import { fetchTwilioToken } from './TwilioApi';

/**
 * Summarise what Twilio attaches to a `connectionError`, for the error message.
 *
 * The payload is `{ terminal, message, httpStatusCode, errorCode }`. Before #717
 * the whole object was handed to Sentry, which serialised it into
 * `extra.__serialized__`; wrapping it in `enhanceError` turned it into a real
 * Error, and Sentry does not serialise an Error's own properties, so everything
 * except `message` was lost. This puts the useful part back, in the message.
 *
 * `terminal` is the field worth having: it is always present, and it says whether
 * the SDK intends to keep retrying. The codes are `null` on the socket-error path
 * that produces most of these, so they only appear for the reply-error family.
 */
export function describeConnectionError(error: unknown): string {
  if (!isObject(error)) {
    return '';
  }

  const { terminal, errorCode, httpStatusCode } = error as {
    terminal?: unknown;
    errorCode?: unknown;
    httpStatusCode?: unknown;
  };

  const parts = [
    typeof terminal === 'boolean' ? `terminal=${terminal}` : '',
    typeof errorCode === 'number' ? `code=${errorCode}` : '',
    typeof httpStatusCode === 'number' ? `status=${httpStatusCode}` : '',
  ].filter(Boolean);

  return parts.length ? ` (${parts.join(' ')})` : '';
}

/**
 * Whether Twilio has given up on this connection.
 *
 * `terminal: true` means the SDK will not retry: the socket stays down until
 * something builds a new client. An expired access token (20104) arrives this
 * way, so this is the difference between "reconnecting" and "the chat is dead
 * and nobody has been told".
 */
export function isTerminalConnectionError(error: unknown): boolean {
  return isObject(error) && (error as { terminal?: unknown }).terminal === true;
}

class TwilioConversationsService {
  client: Client | null = null;
  identity = '';
  subscriptions: Record<string, Function> = {};
  hasConnectionError = false;
  /**
   * Subscribers notified when a connection drops in a way the SDK will not
   * retry. A Set rather than an array so that unregistering is by identity and
   * cannot remove the wrong entry; note it does not dedupe the hook below,
   * which passes a fresh closure on each mount.
   */
  private connectionLostHandlers = new Set<(error: Error) => void>();
  isConnecting = false;
  private token: string | null = null;
  private tokenIsDirty = false;
  clientPromise: Promise<Client> | null = null;
  conversations: Conversation[] = [];

  private canReuseClient(identity: string) {
    return (
      this.client?.connectionState === 'connected' &&
      identity === this.identity &&
      !this.tokenIsDirty
    );
  }

  private async connect(identity: string) {
    if (this.isConnecting) {
      await this.waitForClientConnection();
    }

    this.log('connect', identity);

    //if identity is the same we can just return the client
    if (this.client && this.canReuseClient(identity) && !this.isTokenExpired()) {
      return this.client;
    }

    // Past the reuse check, so whatever client we are holding is not the one we
    // are about to return: a different identity (one may have been built while
    // we waited above), a connection that is no longer `connected`, an expired
    // cached token, or a token update the SDK never accepted. Tear it down
    // before building a new one so we never leak a live client / listeners and
    // never overwrite `this.client` silently -- an orphaned client keeps its
    // socket and its `messageAdded` listener, and that listener fans out into
    // the service-level `subscriptions` map, so it goes on delivering every
    // message alongside the new client.
    // Claim the slot before the first `await` below, not after it. `shutdown()`
    // is awaited, and while `isConnecting` is still false a second connect()
    // walks straight past the guard at the top of this method, finds
    // `this.client` already nulled, skips both the reuse check and this
    // teardown, and starts building its own client -- then this call resumes
    // and overwrites it, orphaning a live client with its listeners attached.
    // Widening the teardown condition above made that window reachable on
    // every rebuild path rather than only on an identity change.
    this.isConnecting = true;

    try {
      if (this.client) {
        this.log('shutting down previous client', this.identity);
        const previousClient = this.client;

        this.client = null;
        previousClient.removeAllListeners();

        // Only drop the cached token when the identity is changing, where it
        // belongs to someone else. On a same-identity rebuild it is still this
        // provider's token, and `getToken()` already refuses an expired one --
        // so keeping it lets the replacement Client reuse it instead of asking
        // `/token` again. (Widening this branch beyond identity changes made an
        // unconditional drop here reach every rebuild path, which it never did
        // before.) The one case where the cached token cannot be trusted is a
        // token update the SDK may not have taken, and `onRenewToken` nulls it
        // there itself.
        if (this.identity !== identity) {
          this.token = null;
        }

        await previousClient.shutdown().catch((error) =>
          Logger.captureException(
            enhanceError(error, {
              prefix: 'TwilioConversationsService.connect.shutdownPreviousClient',
            })
          )
        );
      }

      this.identity = identity;
      this.log('connecting', identity);

      const token = await this.getToken();

      this.log(`token valid until ${getJwtTokenExpirationDate(token) ?? 'unknown (undecodable)'}`);

      // `new Client()` returns an uninitialized client. Await the `initialized`
      // event before proceeding — if init fails, `initFailed` rejects with the
      // real error instead of letting the SDK swallow it silently inside
      // `_ensureReady`, which would later crash `getSubscribedConversations()`
      // with `Cannot read properties of undefined (reading 'then')`.
      // Note: `Client.create()` is @deprecated in v2.6.2 — use constructor + events.
      this.client = new Client(token);
      await this.waitForClientInit(this.client);

      this.log('client created');
      this.conversations = await this.getSubscribedConversations();
      this.log('subscribed conversations', this.conversations.length.toString());
      this.tokenIsDirty = false;
      // We are connected, so whatever failed before no longer describes the
      // current state. Left set, the flag would report a recovered connection
      // as broken for the rest of the session.
      this.hasConnectionError = false;
      this.log('connected');

      this.client
        .removeAllListeners()
        .on('tokenAboutToExpire', this.onRenewToken)
        .on('tokenExpired', this.onRenewToken)
        .on('messageAdded', (payload: any) =>
          Object.values(this.subscriptions).forEach((fn) => fn(payload))
        )
        .on('connectionError', (error: unknown) => {
          const enhancedError = enhanceError(error, {
            prefix: `TwilioConversationsService.connectionError${describeConnectionError(error)}`,
          });

          this.log(enhancedError);

          // Every one of these is reported, `terminal` included in the message
          // rather than used to filter. A non-terminal error means twilsock will
          // retry, not that it will succeed: when the retrier gives up it calls
          // `disconnect()` and emits no `connectionError` at all, and the 401 and
          // expired-token closes do the same. Suppressing the non-terminal ones
          // would leave a permanently dead connection reported by nothing.
          Logger.captureException(enhancedError);

          // Reporting is not handling. A non-terminal error is the SDK's to
          // retry, but a terminal one leaves the socket down for good, and that
          // needs acting on rather than only recording: `hasConnectionError`
          // alone is inert, so a provider's chat could go quiet mid-session
          // with no reconnect and nothing shown. Drop the cached token so the
          // next connect() mints a fresh one -- a 20104 means Twilio considers
          // the token dead whatever our own `exp` arithmetic thinks -- and tell
          // the subscribers, which is what surfaces it to the user.
          if (!isTerminalConnectionError(error)) {
            return;
          }

          this.hasConnectionError = true;
          this.token = null;
          // Only here, not on every connectionError. twilsock retries a
          // non-terminal error using its own in-memory credential and never
          // reads this key, so evicting it there achieved nothing except a
          // needless /token refetch after the next reload.
          clearTwilioToken();

          this.connectionLostHandlers.forEach((handler) => {
            try {
              handler(enhancedError);
            } catch (handlerError) {
              // One bad subscriber must not stop the others being told.
              Logger.captureException(
                enhanceError(handlerError, {
                  prefix: 'TwilioConversationsService.connectionLostHandler',
                })
              );
            }
          });
        });

      return this.client;
    } catch (error) {
      // If we fail after the client was created (e.g. getSubscribedConversations
      // throws), tear it down. Otherwise the live client/listeners leak: the next
      // connect() for this same identity skips the identity-change shutdown branch
      // and silently overwrites this.client, orphaning the previous connection.
      if (this.client) {
        const partialClient = this.client;

        this.client = null;
        this.token = null;
        // The persisted copy too. A token Twilio rejects during init -- the
        // 20104 this PR is about -- fails here rather than through
        // `connectionError`, and clearing only the in-memory one leaves
        // sessionStorage holding a token whose `exp` still passes our check,
        // so the next connect reads the same dead token and fails identically.
        clearTwilioToken();
        partialClient.removeAllListeners();
        await partialClient.shutdown().catch((err) =>
          Logger.captureException(
            enhanceError(err, {
              prefix: 'TwilioConversationsService.connect.shutdownPartialClient',
            })
          )
        );
      }

      throw error;
    } finally {
      // Always clear the connecting flag, even if token fetch or subscription
      // fails, so a failed connect doesn't wedge every future connect() call.
      this.isConnecting = false;
    }
  }

  private waitForClientInit(client: Client, timeoutMs = 15_000): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      let settled = false;
      let timer: ReturnType<typeof setTimeout> | undefined = undefined;

      const onInitialized = () => settle(() => resolve());
      const onInitFailed = ({ error }: any) => {
        const err =
          error instanceof Error
            ? error
            : new Error((error as any)?.message ?? 'Twilio client initialization failed');

        settle(() => reject(err));
      };

      const cleanup = () => {
        if (timer) clearTimeout(timer);
        (client as any).off?.('initialized', onInitialized);
        (client as any).off?.('initFailed', onInitFailed);
        (client as any).removeListener?.('initialized', onInitialized);
        (client as any).removeListener?.('initFailed', onInitFailed);
      };

      const settle = (fn: () => void) => {
        if (settled) return;
        settled = true;
        cleanup();
        fn();
      };

      timer = setTimeout(() => {
        settle(() => reject(new Error('Timed out waiting for Twilio client initialization')));
      }, timeoutMs);

      client.on('initialized', onInitialized);
      client.on('initFailed', onInitFailed);
    });
  }

  private async waitForClientConnection(): Promise<void> {
    return new Promise((resolve, reject) => {
      let attemptsLeft = 20;
      const interval = setInterval(() => {
        if (!this.isConnecting) {
          clearInterval(interval);
          resolve();

          return;
        }

        attemptsLeft--;

        if (attemptsLeft <= 0) {
          clearInterval(interval);
          // Reject rather than resolve(false): connect() awaits this without
          // inspecting a return value, so a silent timeout would let it proceed
          // and build a second client while the previous connect is still
          // in-flight — overlapping connects with competing
          // `finally { isConnecting = false }`, leaking clients/listeners.
          // Rejecting makes the awaiting connect() bail out instead. It throws
          // before setting `isConnecting`/building a client, so the in-flight
          // connect is left untouched, and every connect() caller already
          // handles the rejection (try/catch or .catch).
          reject(new Error('Timed out waiting for in-flight Twilio client connection'));
        }
      }, 1000);
    });
  }

  private isTokenExpired() {
    if (!this.token) {
      this.log('token not found');

      return true;
    }

    // Same margin as the sessionStorage cache: this decides whether to reuse
    // `this.token`, and a token with seconds left is not reusable.
    const isExpired = isJwtTokenExpired(this.token, TOKEN_EXPIRY_MARGIN_SECONDS);

    if (isExpired) {
      this.log('token expired');
    }

    return isExpired;
  }

  private async getToken() {
    if (this.isTokenExpired()) {
      this.token = null;
    }

    if (!this.token) {
      this.log('getting a token');
      this.token = await getTwilioToken(this.identity);
      this.tokenIsDirty = true;
    }

    if (!this.token) {
      throw new Error('Twilio token not found');
    }

    return this.token;
  }

  // Arrow-function field so `this` stays bound to the service when the Twilio
  // SDK invokes it as an event listener (`tokenAboutToExpire` / `tokenExpired`).
  // Without binding, `this` would be the SDK client and `this.identity` would be
  // undefined, silently breaking token rollover.
  private onRenewToken = async () => {
    let renewed: string | null | undefined;

    try {
      clearTwilioToken();
      renewed = await getTwilioToken(this.identity);
    } catch (error) {
      // The fetch leg. Nothing was said to the SDK, so the client is exactly as
      // it was and stays reusable -- leave the cached token alone and just
      // report. It carries its own prefix so it cannot group with the update
      // leg below: the two mean different things and want different fixes, and
      // collapsing unlike causes under one prefix is how three separate
      // `connectionError` families ended up in a single Sentry issue.
      Logger.captureException(
        enhanceError(error, { prefix: 'TwilioConversationsService.onRenewToken.fetch' })
      );

      return;
    }

    if (!renewed) {
      return;
    }

    try {
      // `updateToken` has to be awaited, and the cache written only once it
      // resolves. It is async all the way down: twilsock's `updateToken`
      // resolves on a `tokenUpdated` event and *rejects* when that event
      // carries an error, which is what every failing `sendInit`/`sendUpdate`
      // emits. Fire-and-forget left that rejection outside this catch: a failed
      // renewal only reached Sentry through its default unhandled-rejection
      // handler, with no prefix and nothing in it saying the failure was a
      // token renewal.
      //
      // Writing `this.token` first was worse than losing the report. It tells
      // `isTokenExpired()` the connection is fresh while the SDK is still
      // holding the previous token, so `canReuseClient()` keeps handing back a
      // client authenticated with a token nobody is renewing, until it expires
      // for real and Twilio closes the socket with "Access Token expired or
      // expiration date invalid".
      await this.updateClientToken(renewed);
      this.token = renewed;
    } catch (error) {
      // Trust nothing about the client's credentials now: the SDK may hold the
      // old token, the new one, or have stopped partway between its twilsock,
      // notification and mcs updates. Dropping the cache and marking it dirty
      // makes the next connect() build a fresh client instead of reusing this
      // one, which is the only state we can actually assert.
      this.token = null;
      this.tokenIsDirty = true;

      Logger.captureException(
        enhanceError(error, { prefix: 'TwilioConversationsService.onRenewToken.updateToken' })
      );
    }
  };

  /**
   * `client.updateToken()` behind a deadline.
   *
   * twilsock resolves its half on a one-shot `tokenUpdated` listener with no
   * timeout of its own, so if that event never arrives the promise never
   * settles. Awaiting it bare would replace a silently dropped renewal with a
   * silently hung one, and `onRenewToken` would sit there while the token it
   * was called to replace expires.
   */
  private async updateClientToken(token: string, timeoutMs = 15_000): Promise<void> {
    const client = this.client;

    if (!client) {
      return;
    }

    let timer: ReturnType<typeof setTimeout> | undefined = undefined;

    try {
      await Promise.race([
        client.updateToken(token),
        new Promise<never>((_resolve, reject) => {
          timer = setTimeout(
            () => reject(new Error('Timed out waiting for Twilio token update')),
            timeoutMs
          );
        }),
      ]);
    } finally {
      // Not `if (timer)`: `clearTimeout(undefined)` is a defined no-op, and
      // gating on truthiness makes the cleanup depend on the handle's value
      // rather than on whether one exists.
      clearTimeout(timer);
    }
  }

  async disconnect() {
    this.log('disconnecting');
    this.identity = '';
    this.isConnecting = false;
    this.token = null;

    const client = this.client;

    if (!client) {
      return;
    }

    this.client = null;

    client.removeAllListeners();

    await client.shutdown();
  }

  private async getSubscribedConversations() {
    const client = this.client!;

    const getNextPage = async (
      paginator: any,
      pageConversations: Conversation[]
    ): Promise<Conversation[]> => {
      if (paginator.hasNextPage) {
        const nextPageConversations = await paginator.nextPage();
        const combinedConversations = pageConversations.concat(nextPageConversations.items);

        return getNextPage(nextPageConversations, combinedConversations);
      }

      return pageConversations;
    };

    const result = await client.getSubscribedConversations();
    const pageConversations = result.items;

    return getNextPage(result, pageConversations);
  }

  /**
   * Listen for a connection loss the SDK will not recover from.
   *
   * Returns an unsubscribe function, so a caller in a React effect can clean up
   * without needing a stable handler identity.
   */
  onConnectionLost(handler: (error: Error) => void) {
    this.connectionLostHandlers.add(handler);

    return () => {
      this.connectionLostHandlers.delete(handler);
    };
  }

  async subscribe(identity: string, fn: Function) {
    await this.connect(identity);
    this.subscriptions[identity] = fn;
  }

  unsubscribe(identity: string) {
    delete this.subscriptions[identity];
  }

  private async getConversationByUniqueName(identity: string, patientIdentity: string) {
    this.log('getConversationByUniqueName', identity, patientIdentity);
    const client = await this.connect(identity);

    return client.getConversationByUniqueName(patientIdentity).catch(() => {
      return null;
    });
  }

  async waitForConversation(identity: string, patientIdentity: string): Promise<Conversation> {
    const client = await this.connect(identity);

    return new Promise((resolve, reject) => {
      // 20 attempts at the 1s interval below. The window has to outlast the
      // conversation-creation path it races: on a patient's first visit
      // `setupConversation` is creating the conversation concurrently, and that
      // is a chain of sequential round-trips — a lookup, a `/token` fetch, a
      // second `Client` that has to initialise, `createConversation`, then two
      // participant adds. Client initialisation alone is allowed up to
      // `waitForClientInit`'s 15s ceiling before it gives up, so at the previous
      // 10 attempts this window could expire while a legitimately slow creation
      // was still well inside its own budget, and report "Conversation not
      // found" for a conversation that was on its way.
      let attemptsLeft = 20;
      // Guard against overlapping ticks: `getConversationByUniqueName` can take
      // longer than the poll interval, so without this a slow lookup would let
      // the next tick fire before the previous one settled — double-counting
      // attempts and firing extra lookups that resolve/reject after the interval
      // is already cleared. Skip a tick while one is still in flight.
      let inFlight = false;

      const interval = setInterval(async () => {
        if (inFlight) {
          return;
        }

        inFlight = true;

        try {
          attemptsLeft--;

          let conversation: Conversation | null = null;

          try {
            conversation = await client.getConversationByUniqueName(patientIdentity);
          } catch (err) {
            const isNotFoundError = err instanceof Error && err.message.includes('Not Found');

            // A "Not Found" error just means the conversation isn't ready yet:
            // keep polling until we run out of attempts. Any other error is fatal,
            // so stop polling and reject immediately (previously the interval kept
            // running and could reject repeatedly).
            if (!isNotFoundError) {
              clearInterval(interval);

              // Reject what was reported, not the raw value. `enhanceError`
              // mutates an Error in place and returns it, so for an Error the
              // two are the same object -- but for anything else it builds a
              // *new* Error and leaves the original untouched, so rejecting
              // `err` would hand the caller an unprefixed non-Error while
              // Sentry got the prefixed one. The exhaustion branch below
              // already rejects the enhanced value.
              const failure = enhanceError(err, {
                prefix: 'TwilioConversationsService.waitForConversation.lookup',
              });

              Logger.captureException(failure);
              reject(failure);

              return;
            }

            if (attemptsLeft <= 0) {
              clearInterval(interval);
              reject(enhanceError(err, { prefix: 'Conversation not found' }));
            }

            return;
          }

          if (conversation) {
            clearInterval(interval);
            resolve(conversation);

            return;
          }

          if (attemptsLeft <= 0) {
            clearInterval(interval);
            reject(new Error('Conversation not found'));
          }
        } finally {
          inFlight = false;
        }
      }, 1000);
    });
  }

  private async invitePatient({
    providerName,
    patientIdentity,
    identity,
  }: {
    identity: string;
    patientIdentity: string;
    providerName: string;
  }) {
    this.log('invitePatient', identity, patientIdentity, providerName);
    const inviteToken = await fetchTwilioToken(patientIdentity);

    const client = new Client(inviteToken);

    try {
      await this.waitForClientInit(client);

      let conversation = await client.getConversationByUniqueName(patientIdentity).catch((e) => {
        Logger.captureException(
          enhanceError(e, {
            prefix: 'TwilioConversationsService.invitePatient.getConversationByUniqueName',
          })
        );
      });

      if (conversation) {
        this.log('Conversation found, adding participant', identity, patientIdentity, providerName);
        // In case of error lets consider that is no participants to avoid crashes
        const convParticipants = await conversation.getParticipants().catch((e) => {
          Logger.captureException(
            enhanceError(e, {
              prefix: 'TwilioConversationsService.invitePatient.getParticipants',
            })
          );

          return [];
        });
        const filtered = convParticipants.filter(
          (participant) => participant.identity === identity
        );

        if (!filtered?.length && identity) {
          await conversation.add(identity, { name: providerName });
        }
      } else {
        this.log(
          'Conversation not found, creating new one',
          identity,
          patientIdentity,
          providerName
        );
        conversation = await client.createConversation({
          uniqueName: patientIdentity,
          friendlyName: patientIdentity,
        });
        await conversation.add(identity, { name: providerName });
        await conversation.add(patientIdentity);
      }

      return conversation;
    } finally {
      await client.shutdown().catch((err) =>
        Logger.captureException(
          enhanceError(err, {
            prefix: 'TwilioConversationsService.invitePatient.shutdown',
          })
        )
      );
    }
  }

  private async updatePatientAttributes({
    patientIdentity,
    attributes,
  }: {
    patientIdentity: string;
    attributes: Record<string, string>;
  }) {
    const token = await fetchTwilioToken(patientIdentity);

    const client = new Client(token);

    try {
      await this.waitForClientInit(client);

      const conv = await client.getConversationByUniqueName(patientIdentity);

      await conv.updateAttributes(attributes);
    } finally {
      await client.shutdown().catch((err) =>
        Logger.captureException(
          enhanceError(err, {
            prefix: 'TwilioConversationsService.updatePatientAttributes.shutdown',
          })
        )
      );
    }
  }

  async setupConversation({
    identity,
    patientIdentity,
    patientId,
    providerName,
  }: {
    identity: string;
    patientIdentity: string;
    patientId: string;
    providerName: string;
  }) {
    this.log(
      '[setupConversation] Checking conversation',
      identity,
      patientIdentity,
      patientId,
      providerName
    );
    let conversation = await this.getConversationByUniqueName(identity, patientIdentity);

    if (!conversation) {
      this.log(
        '[setupConversation] Conversation not found, inviting patient',
        identity,
        patientIdentity,
        providerName
      );
      conversation = await this.invitePatient({ identity, patientIdentity, providerName });
    }
    {
      this.log('[setupConversation] Conversation found', identity, patientIdentity);
    }

    await this.updateConversationAttributes(conversation, patientIdentity, patientId);
  }

  private async updateConversationAttributes(
    conversation: Conversation,
    patientIdentity: string,
    patientId: string
  ) {
    const attributes = await conversation.getAttributes();

    if (!isObject(attributes) || !(attributes as JSONObject).patientId) {
      await this.updatePatientAttributes({
        patientIdentity,
        attributes: {
          patientId,
        },
      });
    }
  }

  log(...messages: unknown[]) {
    Logger.debug('[twilio]:', ...messages);
  }
}

export default new TwilioConversationsService();
